import { useAuth0 } from '@auth0/auth0-react';
import {
  Route,
  Routes,
  useNavigate,
  useLocation,
  Navigate,
} from 'react-router-dom';
import { useEffect, type ReactNode } from 'react';
import {
  RootState,
  setToken,
  setUserDetails,
  useAppDispatch,
  initializeChatTokenRefresh,
  resetChatTokenRefresh,
  useGetGlobalSettingsQuery,
} from '@circuitry-ai/doc-data';
import { useSelector } from 'react-redux';
import Layout from './layout/Layout';
import Login from './Login';
import { NextUIProvider } from '@nextui-org/react';
import { AgentCircuitLoader } from '../components/AgentCircuitLoader';

import {
  AdvisorList,
  AdvisorView,
  Advisorknowledge,
  ConversationHistory,
  ConversationMessages,
  Dashboard,
  Knowledge,
  MetadataSettings,
  DomainDictionary,
  Settings,
  AnalystBoard,
  AIWorkerList,
  AIWorkerView,
  Triage,
  WorkflowBuilder,
} from './lazyload/lazyload';
import {
  ClaimIntakePage,
  CtyToast,
  FileViewer,
  FlowEditorWrapper,
  TemplatePreviewPage,
  ThemeProvider,
  useSyncDefaultAdvisorPalMode,
  useTheme,
  RumSettingsSync,
} from '@circuitry-ai/doc-ui';
import { Userpilot } from 'userpilot';
import {
  EmbedZapier,
  Score,
  AgentView,
  AgentsPage,
  ActionsPage,
  ActionView,
  AgentUxgenieTemplateViewPage,
} from '@circuitry-ai/doc-agent';
import 'viewerjs/dist/viewer.css';
import { Analystpage } from '@circuitry-ai/doc-analyst';
import UXGenie from './UXGenie';
import UXGeniePage from './UXGeniePage';
import UXGenieView from './UXGenieView';
import UXGenieTemplateViewer from './UXGenieTemplateViewer';
import FormGenie from './genie/formgenie/FormGenie';
import FormGeniePage from './genie/formgenie/FormGeniePage';
import FormGenieView from './genie/formgenie/FormGenieView';
import SmallGenie from './genie/smallgenie/SmallGenie';
import { useSessionTimeout } from '../hooks/useSessionTimeout';
import { SessionWarningModal } from '../components/SessionWarningModal';
import HistoryPage from './HistoryPage';
import ClaimROViewer from './ClaimROViewer';
import DataPage from './DataPage';
import SchemaMetricsView from './data/SchemaMetricsView';
import { TemplateProvider } from '../contexts/TemplateContext';
import FTIRScreenReplica from './FTIRScreenReplica';
import JobCardScreenReplica from './JobCardScreenReplica';


export function App() {
  const {
    isLoading,
    isAuthenticated,
    getIdTokenClaims,
    getAccessTokenSilently,
    user,
  } = useAuth0();
  const dispatch = useAppDispatch();
  const token = useSelector((state: RootState) => state.auth.token);
  const { data: tenantRumSettings } = useGetGlobalSettingsQuery(undefined, { skip: !token });
  const navigate = useNavigate();
  const location = useLocation();

  // Session timeout management - hardcoded to 60 seconds for testing
  const { showWarningModal, handleContinueSession, handleEndSession } =
    useSessionTimeout(60);
  useEffect(() => {
    Userpilot.reload();
  }, [location]);
  useEffect(() => {
    if (isAuthenticated) {
      Userpilot.identify(user?.name ?? 'guest', {
        name: user?.nickname,
        email: user?.email,
        created_at: user?.updated_at,
        organization: user?.org_name,
        company: {
          name: user?.org_name,
        },
      });
      // Initialize chat token refresh utility with Auth0's getAccessTokenSilently
      if (getAccessTokenSilently) {
        initializeChatTokenRefresh(getAccessTokenSilently);
      }

      if (token === null) {
        const fetchToken = async () => {
          const token = await getIdTokenClaims();
          dispatch(setToken(token?.__raw));
          dispatch(
            setUserDetails({
              details: user,
              isAuthenticated: isAuthenticated,
              isSSOUser: user?.sub?.includes('samlp|'),
            })
          );
        };
        fetchToken();
      }
    } else {
      // Ensure chat token refresh via Auth0 is disabled for guests / logged-out users
      resetChatTokenRefresh();
    }
  }, [
    isAuthenticated,
    dispatch,
    getIdTokenClaims,
    getAccessTokenSilently,
    token,
    user,
  ]);
  const showLoader = isLoading;
  let content;
  if (showLoader) {
    content = <AgentCircuitLoader />;
  } else if (isAuthenticated) {
    content = (
      <>
        <PalDefaultModeSync />
        <Routes>
        {/* Template preview in new tab: no Layout (modal content only) */}
        <Route path="/uxgenie-view" element={<TemplatePreviewPage />} />
        <Route
          path="/agents/history/:aiworker_id/:entity_id/:aiworker_usage_id"
          element={<AgentUxgenieTemplateViewPage />}
        />
        <Route
          path="*"
          element={
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/advisors" element={<AdvisorList />} />
                <Route path="/advisors/:advisor_id" element={<AdvisorView />} />
                <Route
                  path="/advisors/createadvisor"
                  element={<AdvisorView />}
                />
                <Route path="/settings/zapier" element={<EmbedZapier />} />
                <Route
                  path="/settings"
                  element={
                    <Settings
                      UXGeniePageComponent={UXGeniePage}
                      UXGenieViewComponent={UXGenieView}
                      UXGenieComponent={UXGenie}
                      UXGenieTemplateViewerComponent={UXGenieTemplateViewer}
                      FormGeniePageComponent={FormGeniePage}
                      FormGenieViewComponent={FormGenieView}
                      FormGenieComponent={FormGenie}
                      SmallGenieComponent={SmallGenie}
                    />
                  }
                />
                <Route
                  path="/settings/uxgenie/view/:aiworker_usage_id"
                  element={
                    <Settings
                      UXGeniePageComponent={UXGeniePage}
                      UXGenieViewComponent={UXGenieView}
                      UXGenieComponent={UXGenie}
                      UXGenieTemplateViewerComponent={UXGenieTemplateViewer}
                      FormGeniePageComponent={FormGeniePage}
                      FormGenieViewComponent={FormGenieView}
                      FormGenieComponent={FormGenie}
                      SmallGenieComponent={SmallGenie}
                    />
                  }
                />
                <Route
                  path="/settings/uxgenie/:aiworker_id/execute"
                  element={
                    <Settings
                      UXGeniePageComponent={UXGeniePage}
                      UXGenieViewComponent={UXGenieView}
                      UXGenieComponent={UXGenie}
                      UXGenieTemplateViewerComponent={UXGenieTemplateViewer}
                      FormGeniePageComponent={FormGeniePage}
                      FormGenieViewComponent={FormGenieView}
                      FormGenieComponent={FormGenie}
                      SmallGenieComponent={SmallGenie}
                    />
                  }
                />
                <Route
                  path="/settings/uxgenie/:aiworker_id/edit/:usage_id"
                  element={
                    <Settings
                      UXGeniePageComponent={UXGeniePage}
                      UXGenieViewComponent={UXGenieView}
                      UXGenieComponent={UXGenie}
                      UXGenieTemplateViewerComponent={UXGenieTemplateViewer}
                      FormGeniePageComponent={FormGeniePage}
                      FormGenieViewComponent={FormGenieView}
                      FormGenieComponent={FormGenie}
                      SmallGenieComponent={SmallGenie}
                    />
                  }
                />
                <Route
                  path="/settings/uxgenie/:mode?"
                  element={
                    <Settings
                      UXGeniePageComponent={UXGeniePage}
                      UXGenieViewComponent={UXGenieView}
                      UXGenieComponent={UXGenie}
                      UXGenieTemplateViewerComponent={UXGenieTemplateViewer}
                      FormGeniePageComponent={FormGeniePage}
                      FormGenieViewComponent={FormGenieView}
                      FormGenieComponent={FormGenie}
                      SmallGenieComponent={SmallGenie}
                    />
                  }
                />
                <Route
                  path="/settings/formgenie/:aiworker_id/execute"
                  element={
                    <Settings
                      UXGeniePageComponent={UXGeniePage}
                      UXGenieViewComponent={UXGenieView}
                      UXGenieComponent={UXGenie}
                      UXGenieTemplateViewerComponent={UXGenieTemplateViewer}
                      FormGeniePageComponent={FormGeniePage}
                      FormGenieViewComponent={FormGenieView}
                      FormGenieComponent={FormGenie}
                      SmallGenieComponent={SmallGenie}
                    />
                  }
                />
                <Route
                  path="/settings/formgenie/:aiworker_id/edit/:usage_id"
                  element={
                    <Settings
                      UXGeniePageComponent={UXGeniePage}
                      UXGenieViewComponent={UXGenieView}
                      UXGenieComponent={UXGenie}
                      UXGenieTemplateViewerComponent={UXGenieTemplateViewer}
                      FormGeniePageComponent={FormGeniePage}
                      FormGenieViewComponent={FormGenieView}
                      FormGenieComponent={FormGenie}
                      SmallGenieComponent={SmallGenie}
                    />
                  }
                />
                <Route
                  path="/settings/formgenie/:mode?"
                  element={
                    <Settings
                      UXGeniePageComponent={UXGeniePage}
                      UXGenieViewComponent={UXGenieView}
                      UXGenieComponent={UXGenie}
                      UXGenieTemplateViewerComponent={UXGenieTemplateViewer}
                      FormGeniePageComponent={FormGeniePage}
                      FormGenieViewComponent={FormGenieView}
                      FormGenieComponent={FormGenie}
                      SmallGenieComponent={SmallGenie}
                    />
                  }
                />
                <Route
                  path="/settings/genie"
                  element={
                    <Settings
                      UXGeniePageComponent={UXGeniePage}
                      UXGenieViewComponent={UXGenieView}
                      UXGenieComponent={UXGenie}
                      UXGenieTemplateViewerComponent={UXGenieTemplateViewer}
                      FormGeniePageComponent={FormGeniePage}
                      FormGenieViewComponent={FormGenieView}
                      FormGenieComponent={FormGenie}
                      SmallGenieComponent={SmallGenie}
                    />
                  }
                />
                <Route
                  path="/settings/schema-mapper"
                  element={
                    <Settings
                      UXGeniePageComponent={UXGeniePage}
                      UXGenieViewComponent={UXGenieView}
                      UXGenieComponent={UXGenie}
                      UXGenieTemplateViewerComponent={UXGenieTemplateViewer}
                      FormGeniePageComponent={FormGeniePage}
                      FormGenieViewComponent={FormGenieView}
                      FormGenieComponent={FormGenie}
                      SmallGenieComponent={SmallGenie}
                    />
                  }
                />
                <Route
                  path="/settings/schema-mapper/:mode"
                  element={
                    <Settings
                      UXGeniePageComponent={UXGeniePage}
                      UXGenieViewComponent={UXGenieView}
                      UXGenieComponent={UXGenie}
                      UXGenieTemplateViewerComponent={UXGenieTemplateViewer}
                      FormGeniePageComponent={FormGeniePage}
                      FormGenieViewComponent={FormGenieView}
                      FormGenieComponent={FormGenie}
                      SmallGenieComponent={SmallGenie}
                    />
                  }
                />
                <Route
                  path="/settings/schema-mapper/:mode/:mapper_id"
                  element={
                    <Settings
                      UXGeniePageComponent={UXGeniePage}
                      UXGenieViewComponent={UXGenieView}
                      UXGenieComponent={UXGenie}
                      UXGenieTemplateViewerComponent={UXGenieTemplateViewer}
                      FormGeniePageComponent={FormGeniePage}
                      FormGenieViewComponent={FormGenieView}
                      FormGenieComponent={FormGenie}
                      SmallGenieComponent={SmallGenie}
                    />
                  }
                />
                <Route
                  path="/settings/:section"
                  element={
                    <Settings
                      UXGeniePageComponent={UXGeniePage}
                      UXGenieViewComponent={UXGenieView}
                      UXGenieComponent={UXGenie}
                      UXGenieTemplateViewerComponent={UXGenieTemplateViewer}
                      FormGeniePageComponent={FormGeniePage}
                      FormGenieViewComponent={FormGenieView}
                      FormGenieComponent={FormGenie}
                      SmallGenieComponent={SmallGenie}
                    />
                  }
                />
                <Route
                  path="/conversations"
                  element={<ConversationHistory />}
                />
                {/* <Route path="/search-with-milvus" element={<ConversationHistoryMilves />} /> */}
                <Route
                  path={`/conversation/:sender_id/:advisor_id`}
                  element={<ConversationMessages />}
                />
                <Route path="/knowledge" element={<Knowledge />} />
                <Route
                  path="/knowledge/metadata-settings"
                  element={<MetadataSettings />}
                />
                <Route
                  path="/knowledge/domain-dictionary"
                  element={<DomainDictionary />}
                />
                <Route
                  path="/knowledge/:advisor_id/:source_id?/:source_type?"
                  element={<Advisorknowledge />}
                />
                <Route
                  path="/file/:advisorId/:fileName"
                  element={<FileViewer />}
                />
                <Route path="/analytics" element={<Analystpage />} />
                <Route path="/analytics/:mode" element={<AnalystBoard />} />
                <Route path="/agents" element={<AgentsPage />} />
                <Route path="/actions" element={<ActionsPage />} />
                <Route path="/actions/:mode" element={<ActionView />} />
                <Route path="/agents/automation" element={<EmbedZapier />} />
                <Route path="/agents/:mode" element={<AgentView />} />
                <Route path="/agents/:mode/:aiworker_id" element={<AgentView />} />
                <Route path="/actions/score" element={<Score />} />
                <Route path="/flow" element={<FlowEditorWrapper />} />
                <Route
                  path="/aiworkers/list/:category"
                  element={<AIWorkerList />}
                />
                <Route
                  path="/aiworkers/create/:category"
                  element={<AIWorkerView />}
                />
                <Route
                  path="/aiworkers/view/:category/:aiworker_id"
                  element={<AIWorkerView />}
                />
                <Route path="/history" element={<HistoryPage />} />
                <Route path="/triage" element={<Triage />} />
                <Route path="/workflows" element={<WorkflowBuilder />} />
                <Route path="/workflows/:id" element={<WorkflowBuilder />} />
                {/* <Route path="/formbuilder" element={<FormBuilderSelector />} />
          <Route path="/test" element={<GeneratedForm />} /> */}
                <Route
                  path="/uxgenie"
                  element={<Navigate to="/settings/uxgenie" replace />}
                />
                <Route path="/uxgenie/:mode" element={<UXGenieView />} />
                <Route
                  path="/uxgenie/:aiworker_id/execute"
                  element={<UXGenie />}
                />
                <Route
                  path="/uxgenie/:aiworker_id/edit/:usage_id"
                  element={<UXGenie />}
                />
                <Route
                  path="/uxgenie/view/:aiworker_usage_id"
                  element={<UXGenieTemplateViewer />}
                />
                <Route
                  path="/genie"
                  element={<Navigate to="/settings/genie" replace />}
                />
                <Route
                  path="/formgenie"
                  element={<Navigate to="/settings/formgenie" replace />}
                />
                <Route path="/formgenie/:mode" element={<FormGenieView />} />
                <Route
                  path="/formgenie/:aiworker_id/execute"
                  element={<FormGenie />}
                />
                <Route
                  path="/formgenie/:aiworker_id/edit/:usage_id"
                  element={<FormGenie />}
                />
                <Route path="/claimroviewer" element={<ClaimROViewer />} />
                <Route path="/data" element={<DataPage />} />
                <Route
                  path="/data/metrics/:schemaId"
                  element={<SchemaMetricsView />}
                />
                <Route path="/job-card" element={<JobCardScreenReplica />} />
                <Route path="/ftir" element={<FTIRScreenReplica />} />
                <Route path="*" element={<Navigate to="/" replace />} />
                <Route path="/schema-mapper" element={<Navigate to="/settings/schema-mapper" replace />} />
                <Route path="/schema-mapper/:mode" element={<Navigate to="/settings/schema-mapper" replace />} />
                <Route path="/schema-mapper/:mode/:mapper_id" element={<Navigate to="/settings/schema-mapper" replace />} />
                <Route
                  path="/claim-intake"
                  element={
                    <ClaimIntakePage isVoiceAgentAccessible />
                  }
                />
              </Routes>
              <CtyToast />
            </Layout>
          }
        />
      </Routes>
      </>
    );
  } else {
    content = <Login />;
  }

  return (
    <ThemeProvider>
      <RumSettingsSync settings={tenantRumSettings} />
      <ThemedNextUIProvider navigate={navigate}>
        <TemplateProvider>{content}</TemplateProvider>
        <SessionWarningModal
          isOpen={showWarningModal}
          onContinue={handleContinueSession}
          onEndSession={handleEndSession}
          warningSeconds={60}
        />
      </ThemedNextUIProvider>
    </ThemeProvider>
  );
}

function PalDefaultModeSync() {
  useSyncDefaultAdvisorPalMode();
  return null;
}

/** NextUIProvider that mirrors the current dark mode class so NextUI's theme tokens resolve correctly. */
function ThemedNextUIProvider({
  navigate,
  children,
}: Readonly<{ navigate: (path: string) => void; children: ReactNode }>) {
  const { isDark } = useTheme();

  // Keep Monaco editors in sync with the theme. We only touch an already-loaded
  // Monaco instance so we never eager-load the heavy editor bundle on pages
  // that don't use it.
  useEffect(() => {
    void import('@monaco-editor/react').then(({ loader }) => {
      const monaco = (loader as { __getMonacoInstance?: () => unknown }).__getMonacoInstance?.();
      if (monaco) {
        (monaco as { editor: { setTheme: (t: string) => void } }).editor.setTheme(isDark ? 'vs-dark' : 'vs');
      }
    }).catch(() => undefined);
  }, [isDark]);

  return (
    <NextUIProvider navigate={navigate} className={isDark ? 'dark text-foreground bg-background' : ''}>
      {children}
    </NextUIProvider>
  );
}
export default App;
